<template>
  <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
    <div class="flex items-center justify-between mb-6">
      <h3 class="text-lg font-semibold text-gray-800">تنظیمات مالیات</h3>
      <button 
        @click="showCreateForm = true"
        class="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
      >
        + افزودن مالیات جدید
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-8">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-brown-600"></div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-8">
      <div class="text-red-600 text-lg mb-4">{{ error }}</div>
      <button 
        @click="loadTaxes"
        class="bg-brown-600 text-white px-6 py-2 rounded-lg hover:bg-brown-700 transition-colors"
      >
        تلاش مجدد
      </button>
    </div>

    <!-- Tax List -->
    <div v-else class="space-y-4">
      <div 
        v-for="tax in taxes" 
        :key="tax.id"
        class="flex items-center justify-between p-4 border border-gray-200 rounded-lg flex-col sm:flex-row gap-3"
        :class="{ 'bg-green-50 border-green-200': tax.isActive }"
      >
        <div class="flex-1">
          <div class="flex items-center space-x-3 space-x-reverse">
            <h4 class="font-medium text-gray-800">{{ tax.name }}</h4>
            <span 
              v-if="tax.isActive"
              class="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full"
            >
              فعال
            </span>
          </div>
          <p class="text-sm text-gray-500 mt-1">{{ tax.description }}</p>
          <div class="flex items-center space-x-4 space-x-reverse mt-2">
            <span class="text-lg font-semibold text-brown-600">{{ tax.percentage }}%</span>
            <span class="text-xs text-gray-400">
              ایجاد شده: {{ formatDate(tax.createdAt) }}
            </span>
          </div>
        </div>
        
        <div class="flex items-center space-x-2 space-x-reverse flex-wrap">
          <button 
            @click="editTax(tax)"
            class="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
          >
            ویرایش
          </button>
          <button 
            @click="deleteTax(tax.id)"
            class="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition-colors"
          >
            حذف
          </button>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="taxes.length === 0" class="text-center py-12">
        <div class="text-gray-500 text-lg mb-4">هیچ مالیاتی تعریف نشده است</div>
        <button 
          @click="showCreateForm = true"
          class="bg-brown-600 text-white px-6 py-2 rounded-lg hover:bg-brown-700 transition-colors"
        >
          افزودن اولین مالیات
        </button>
      </div>
    </div>

    <!-- Create/Edit Tax Modal -->
    <div v-if="showCreateForm || editingTax" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 w-96 max-w-md">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-gray-800">
            {{ editingTax ? 'ویرایش مالیات' : 'افزودن مالیات جدید' }}
          </h3>
          <button 
            @click="closeForm"
            class="text-gray-400 hover:text-gray-600"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        <form @submit.prevent="saveTax" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">نام مالیات:</label>
            <input 
              v-model="taxForm.name"
              type="text"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brown-500 focus:border-transparent"
              placeholder="مثال: مالیات بر ارزش افزوده"
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">درصد مالیات:</label>
            <input 
              v-model="taxForm.percentage"
              type="number"
              step="0.01"
              min="0"
              max="100"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brown-500 focus:border-transparent"
              placeholder="مثال: 9"
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">توضیحات:</label>
            <textarea 
              v-model="taxForm.description"
              rows="3"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brown-500 focus:border-transparent"
              placeholder="توضیحات اختیاری..."
            ></textarea>
          </div>
          
          <div v-if="editingTax">
            <label class="flex items-center">
              <input 
                v-model="taxForm.isActive"
                type="checkbox"
                class="rounded border-gray-300 text-brown-600 focus:ring-brown-500"
              />
              <span class="mr-2 text-sm text-gray-700">فعال</span>
            </label>
          </div>
          
          <div class="flex space-x-3 space-x-reverse">
            <button 
              type="button"
              @click="closeForm"
              class="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors"
            >
              انصراف
            </button>
            <button 
              type="submit"
              :disabled="saving"
              class="flex-1 bg-brown-600 text-white px-4 py-2 rounded-lg hover:bg-brown-700 transition-colors disabled:opacity-50"
            >
              {{ saving ? 'در حال ذخیره...' : (editingTax ? 'ویرایش' : 'افزودن') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { taxAPI } from '@/services/api'

export default {
  name: 'TaxSettings',
  setup() {
    const taxes = ref([])
    const loading = ref(true)
    const error = ref(null)
    const showCreateForm = ref(false)
    const editingTax = ref(null)
    const saving = ref(false)
    
    const taxForm = ref({
      name: '',
      percentage: 0,
      description: '',
      isActive: true
    })

    const loadTaxes = async () => {
      try {
        loading.value = true
        error.value = null
        const response = await taxAPI.getAll()
        taxes.value = response.data
      } catch (err) {
        console.error('Error loading taxes:', err)
        error.value = 'خطا در بارگذاری مالیات‌ها'
      } finally {
        loading.value = false
      }
    }

    const editTax = (tax) => {
      editingTax.value = tax
      taxForm.value = {
        name: tax.name,
        percentage: tax.percentage,
        description: tax.description || '',
        isActive: tax.isActive
      }
    }

    const saveTax = async () => {
      try {
        saving.value = true
        
        if (editingTax.value) {
          await taxAPI.update(editingTax.value.id, taxForm.value)
        } else {
          await taxAPI.create(taxForm.value)
        }
        
        await loadTaxes()
        closeForm()
      } catch (err) {
        console.error('Error saving tax:', err)
        alert('خطا در ذخیره مالیات')
      } finally {
        saving.value = false
      }
    }

    const deleteTax = async (taxId) => {
      if (!confirm('آیا از حذف این مالیات اطمینان دارید؟')) {
        return
      }
      
      try {
        await taxAPI.delete(taxId)
        await loadTaxes()
      } catch (err) {
        console.error('Error deleting tax:', err)
        alert('خطا در حذف مالیات')
      }
    }

    const closeForm = () => {
      showCreateForm.value = false
      editingTax.value = null
      taxForm.value = {
        name: '',
        percentage: 0,
        description: '',
        isActive: true
      }
    }

    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('fa-IR')
    }

    onMounted(() => {
      loadTaxes()
    })

    return {
      taxes,
      loading,
      error,
      showCreateForm,
      editingTax,
      saving,
      taxForm,
      loadTaxes,
      editTax,
      saveTax,
      deleteTax,
      closeForm,
      formatDate
    }
  }
}
</script>

<style scoped>
/* Custom styles if needed */
</style>
